import * as definition from '@/lib/highlightMappings'
import _ from 'lodash'

// 定数
const topParentHighlightId = 'preview'
const highlightIdPrefix = 'highlight'
const areaHighlightIds = [
  definition.targetIdMap.mainArticle.photo,
  definition.targetIdMap.freeTopics.photo,
  definition.targetIdMap.feature.prefix,
  definition.targetIdMap.companyInfo.intro.image,
  definition.targetIdMap.companyInfo.images.image,
  definition.targetIdMap.environment.prefix
]
const highlightClass = {
  bossC: 'boss-c',
  bossS: 'boss-s',
  change: 'change'
}

// HTML IDの取得
function getId (node) {
  let parent = node.parentNode
  while (!parent.id) {
    parent = parent.parentNode
  }
  return parent.id
}

// ハイライト済み判定
function isHighlighted (selection) {
  // 開始ノードまたは終了ノードの親idがhighlight-***であるか
  return getId(selection.anchorNode).indexOf(highlightIdPrefix) > -1 ||
      getId(selection.focusNode).indexOf(highlightIdPrefix) > -1
}

// 画面上のテキストハイライトを削除
function deleteTextHighlight (node) {
  let targetNode = node
  while (targetNode.tagName !== 'MARK') { targetNode = targetNode.parentNode }

  while (targetNode.firstChild) {
    targetNode.parentNode.insertBefore(targetNode.firstChild, targetNode)
  }
  targetNode.remove()
}

// ハイライト位置座標を記録
function setOffsetPosition (positionObject, highlightElement) {
  positionObject.top = highlightElement.offsetTop
  positionObject.left = highlightElement.offsetLeft

  let parent = highlightElement.parentElement
  if (!positionObject.top && !positionObject.left) {
    // 自分の位置が取れない場合、親位置を記録する
    positionObject.top = parent.offsetTop
    positionObject.left = parent.offsetLeft
  }

  // 多重ハイライトを考慮
  while (parent.tagName === 'MARK' || parent.id === '') { parent = parent.parentElement }
  if (parent.tagName === 'TD') {
    // td内のハイライトの場合のみpage上の座標を取れないため、親位置を算出
    positionObject.parentTop = parent.offsetTop
  } else {
    // それ以外は親位置を利用したくないため０とする
    positionObject.parentTop = 0
  }
  return positionObject
}

// ハイライトIDの生成
function createHighlightId (target, index) {
  return `${highlightIdPrefix}-${target}-${index}`
}

// ハイライト対象文字列の切り詰め
function truncateHighlightText (highlightTargetText) {
  return highlightTargetText.length > 50 ? highlightTargetText.substring(0, 50) + '...' : highlightTargetText
}

// ハイライト用クラス名取得
function getHighlightClass (isCompanyStaff) {
  return isCompanyStaff ? highlightClass.bossC : highlightClass.bossS
}

// エリアハイライト対象か？(前方一致)
function isAreaHighlight (target) {
  return areaHighlightIds.some(areaHighlightId => target.indexOf(areaHighlightId) === 0)
}

// エリアハイライト実施
function areaHighlight (element, highlightClass = '') {
  const targetRange = document.createRange()
  targetRange.selectNode(element)
  const newHighlight = document.createElement('span')
  newHighlight.className = `pega-highlight ${highlightClass}`
  if (element.tagName === 'IMG') newHighlight.className += ' img'
  targetRange.surroundContents(newHighlight)
  return newHighlight
}

// エリアハイライト削除
function deleteAreaHighlight (element) {
  const targetNode = element.parentNode
  targetNode.parentNode.insertBefore(element, targetNode)
  targetNode.remove()
}

// ハイライトの削除(スレッドベース)
function deleteHighlight (thread) {
  const targetId = thread.target
  const element = document.getElementById(targetId)
  if (isAreaHighlight(targetId)) {
    deleteAreaHighlight(element)
  } else {
    const target = getTargetRange(thread, element)
    if (!target) return
    deleteTextHighlight(target.endContainer.parentNode)
  }
}

// 対象のRangeを取得
function getTargetRange (commentHighlight, element) {
  const targetRange = document.createRange()
  const startNodeInfo = getTargetNode(element, commentHighlight.highlight.start)
  const endNodeInfo = getTargetNode(element, commentHighlight.highlight.end)
  if (cantGetRangeTargetNode(startNodeInfo, endNodeInfo) ||
          isZeroRangeHighlight(startNodeInfo, endNodeInfo)) {
    // 処理をskipする
    return null
  }
  targetRange.setStart(startNodeInfo.node, startNodeInfo.offset)
  if (endNodeInfo.node.length < endNodeInfo.offset) {
    targetRange.setEnd(endNodeInfo.node, endNodeInfo.node.length)
  } else {
    targetRange.setEnd(endNodeInfo.node, endNodeInfo.offset)
  }
  return targetRange
}

// 対象のNodeを取得
function getTargetNode (element, offset) {
  // elementが取れない場合には、idが変わっているため処理をskipする
  if (!element) return null

  // elementの先頭から走査し、offset分考慮したnodeを検出する。
  // また、offsetの値も、対象node内のoffsetに合わせて算出する

  // 記録用オブジェクトを初期化
  const target = {
    node: undefined,
    textCount: 0,
    lastCount: 0
  }

  _.forEach(element.childNodes, (node) => {
    target.node = node
    if (target.node.length) {
      target.lastCount = target.textCount
      target.textCount += target.node.length
    } else {
      // 子ノードが入れ子の場合中を走査
      searchChildNodes(target.node.childNodes, offset, target)
      // 入れ子ノード走査中に対象ノードが見つかった場合、親ループをbreakするため再度同一の判定を実施
    }
    if (target.textCount >= offset) return false // 処理終了
  })
  return { node: target.node, offset: offset - target.lastCount }
}

// 子ノードの検索
function searchChildNodes (childNodes, offset, target) {
  _.forEach(childNodes, (childNode) => {
    if (childNode.nodeName === '#text') {
      target.node = childNode
      target.lastCount = target.textCount
      target.textCount += target.node.length
      if (target.textCount >= offset) return false // 処理終了
    } else if (childNode.firstChild) {
      searchChildNodes(childNode.childNodes, offset, target)
      // 入れ子ノード走査中に対象ノードが見つかった場合、親ループをbreakするため再度同一の判定を実施
      if (target.textCount >= offset) return false // 処理終了
    }
  })
}

// ターゲットノードが正しく取得できない
function cantGetRangeTargetNode (startNodeInfo, endNodeInfo) {
  return !startNodeInfo || !startNodeInfo.node ||
          !endNodeInfo || !endNodeInfo.node ||
          startNodeInfo.node.length < startNodeInfo.offset
}

// 対象ノード開始終了が同値になっている(例：文書変更の結果、末尾0文字ハイライト)
function isZeroRangeHighlight (startNodeInfo, endNodeInfo) {
  return startNodeInfo.node === endNodeInfo.node &&
          startNodeInfo.offset === endNodeInfo.offset
}

exports.topParentHighlightId = topParentHighlightId
exports.highlightIdPrefix = highlightIdPrefix
exports.highlightClass = highlightClass
exports.getId = getId
exports.isHighlighted = isHighlighted
exports.deleteTextHighlight = deleteTextHighlight
exports.setOffsetPosition = setOffsetPosition
exports.createHighlightId = createHighlightId
exports.truncateHighlightText = truncateHighlightText
exports.getHighlightClass = getHighlightClass
exports.isAreaHighlight = isAreaHighlight
exports.areaHighlight = areaHighlight
exports.deleteAreaHighlight = deleteAreaHighlight
exports.deleteHighlight = deleteHighlight
exports.getTargetRange = getTargetRange
exports.getTargetNode = getTargetNode
exports.searchChildNodes = searchChildNodes
exports.cantGetRangeTargetNode = cantGetRangeTargetNode
exports.isZeroRangeHighlight = isZeroRangeHighlight
